import type { Column } from '@shared/types/data-table'
import type { PayoutScheduleProfile } from '@features/payout-schedules/types/payout-schedule.types'
import type { ColumnManagementConfig } from '@shared/lib/tables/utils/column-management'
import { createIdColumn, createTextColumn, createCustomColumn, createDateColumn, createStatusColumn } from './column-factories'
import { PROGRAM_OPTIONS } from '@shared/constants/billing-constants'

const MODEL_LABELS: Record<string, string> = {
  'same-day-payout': 'Same Day',
  'pass-through-payout': 'Pass-Through',
}

const FREQUENCY_LABELS: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Bi-weekly',
  monthly: 'Monthly',
  'on-demand': 'On demand',
}

export const BILLING_PAYOUT_COLUMNS: Record<string, Column<PayoutScheduleProfile>> = {
  id: createIdColumn<PayoutScheduleProfile>(),
  name: createTextColumn<PayoutScheduleProfile>(
    'name' as keyof PayoutScheduleProfile,
    'Name',
    140,
    'text-sm font-medium',
  ),
  status: { ...createStatusColumn<PayoutScheduleProfile>('billing-payout'), header: '' },
  description: createTextColumn<PayoutScheduleProfile>(
    'description' as keyof PayoutScheduleProfile,
    'Description',
    220,
    'text-sm text-muted-foreground',
  ),
  model: createCustomColumn<PayoutScheduleProfile>(
    'model',
    'Model',
    140,
    (profile: PayoutScheduleProfile) => (
      <span className="text-sm text-muted-foreground">
        {MODEL_LABELS[profile.model] || profile.model}
      </span>
    ),
  ),
  frequency: createCustomColumn<PayoutScheduleProfile>(
    'frequency',
    'Frequency',
    100,
    (profile: PayoutScheduleProfile) => (
      <span className="text-sm text-muted-foreground">
        {FREQUENCY_LABELS[profile.frequency] || profile.frequency}
      </span>
    ),
  ),
  program: createCustomColumn<PayoutScheduleProfile>(
    'program',
    'Program',
    140,
    (profile: PayoutScheduleProfile) => {
      const label = PROGRAM_OPTIONS.find((p) => p.value === profile.program)?.label || profile.program
      return <span className="text-sm text-muted-foreground">{label}</span>
    },
  ),
  merchant: createTextColumn<PayoutScheduleProfile>(
    'merchant' as keyof PayoutScheduleProfile,
    'Merchant',
    140,
    'text-sm text-muted-foreground',
  ),
  createdDate: createDateColumn<PayoutScheduleProfile>(
    'createdDate' as keyof PayoutScheduleProfile,
    'Created',
    140,
    false,
  ),
  effectiveDate: createDateColumn<PayoutScheduleProfile>(
    'effectiveDate' as keyof PayoutScheduleProfile,
    'Effective',
    140,
    false,
  ),
  updatedDate: createDateColumn<PayoutScheduleProfile>(
    'updatedDate' as keyof PayoutScheduleProfile,
    'Updated',
    140,
    false,
  ),
}

const BILLING_PAYOUT_COLUMN_ORDER = ['id', 'name', 'status', 'description', 'model', 'frequency', 'program', 'merchant', 'createdDate', 'effectiveDate', 'updatedDate'] as const

const BILLING_PAYOUT_STATUS_CONFIG: Record<string, readonly string[]> = {
  all: BILLING_PAYOUT_COLUMN_ORDER,
  active: BILLING_PAYOUT_COLUMN_ORDER,
  draft: BILLING_PAYOUT_COLUMN_ORDER,
  archived: BILLING_PAYOUT_COLUMN_ORDER,
}

export const getBillingPayoutColumns = (status: string = 'all'): Column<PayoutScheduleProfile>[] => {
  const keys =
    BILLING_PAYOUT_STATUS_CONFIG[status as keyof typeof BILLING_PAYOUT_STATUS_CONFIG] ||
    BILLING_PAYOUT_STATUS_CONFIG.all
  return keys.map((key) => BILLING_PAYOUT_COLUMNS[key]).filter(Boolean)
}

export const getBillingPayoutColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(BILLING_PAYOUT_STATUS_CONFIG[status as keyof typeof BILLING_PAYOUT_STATUS_CONFIG] || BILLING_PAYOUT_STATUS_CONFIG.all)
  return Object.entries(BILLING_PAYOUT_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ['id', 'name', 'status'].includes(key),
    order: index
  }))
}
